import {
  AnnouncementAlertStyle,
  CreditPlanResultItem,
  CreditPlanFailureReason,
  CreditPlanStatus,
  ICLEStateTransitionOption,
  ThumbValue,
  UserTrackerData,
  CreditPlanType,
  CreditPlanResponseModel,
  CreditBreakdownCreditType
} from '@/@types/client-api';
import type { ThumbFeedbackValue } from '@/components/ui/Feedback/ThumbFeedbackButtons';
import LoadingDots from '../../Buttons/LoadingDots';
import ChevronRightIcon from '@/assets/icons/chevron-right.svg?react';
import { existsCookie } from '@/utils/cookie-list-values';
import {
  CREDIT_PLANS_PRACTICEAREA_COOLDOWN_COOKIE_NAME,
  CreditPlanJurisdiction
} from './constants';
import { JurisdictionSpecificMessageBlock } from '@/@types/content';
import { IContent } from '@/@types/cms';
import { SpecialStatusSelector, TrackerQueryParamKeys } from '../../CreditTracker/util/constants';
import { ComplianceFormFields } from '../../CreditTracker/Forms/ComplianceForm';
import { templateString } from '@/utils/helpers';
import { getIsSavedToLibrary } from '../../SearchResults/Snippets/helpers';

const CREDIT_PLAN_CARD_IMAGE_DIMENSIONS = {
  portrait: {
    ['1x']: { width: 576, height: 864 },
    ['2x']: { width: 1152, height: 1728 }
  },
  landscape: {
    ['1x']: { width: 1280, height: 417 },
    ['2x']: { width: 2560, height: 834 }
  }
} as const;

export const mapThumbFeedbackUIValueToThumbValue = (thumbValue: ThumbFeedbackValue): ThumbValue => {
  return thumbValue === 'thumbsUp' ? ThumbValue._1 : ThumbValue._0;
};

const buildCreditPlanRegionImageUrl = (
  regionCode: string,
  isTransitional: boolean,
  orientation: 'landscape' | 'portrait',
  width: number,
  height: number
) => {
  const transitionalParam = `${isTransitional ? '?isTransitionalOrNewlyAdmitted=true' : '?isTransitionalOrNewlyAdmitted=false'}`;
  return `${import.meta.env.VITE_CONTENT_DELIVERY_API}/api/client/credit-plans/image/${regionCode}/${orientation}${transitionalParam}&width=${width}&height=${height}`;
};

/**
 * API returns status as number; map to string enum for UI consumption
 * @param n number status from API
 * @returns CreditPlanStatus enum value or undefined
 */
export const mapCreditPlanStatus = (n: number | undefined): CreditPlanStatus | undefined => {
  if (n === undefined) return undefined;
  const map: Record<number, CreditPlanStatus> = {
    0: CreditPlanStatus.STARTED,
    1: CreditPlanStatus.EXECUTING,
    2: CreditPlanStatus.COMPLETED,
    3: CreditPlanStatus.FAILED
  };
  return map[n];
};

export const mapCreditPlanFailureReason = (
  n: number | undefined
): CreditPlanFailureReason | undefined => {
  if (n === undefined) return undefined;
  const map: Record<number, CreditPlanFailureReason> = {
    0: CreditPlanFailureReason.GENERAL,
    1: CreditPlanFailureReason.NO_CREDIT_REQUIREMENTS,
    2: CreditPlanFailureReason.NO_PROGRAMS_FOUND,
    3: CreditPlanFailureReason.NO_PROGRAMS_PICKED_FOR_PLAN
    // 4: CreditPlanFailureReason.UNABLE_TO_RETRIEVE_PLAN,
    // 5: CreditPlanFailureReason.UNABLE_TO_START_PLAN_GENERATION
  };
  return map[n];
};

export const mapCreditPlanAlertMessage = (
  n: number | undefined
): AnnouncementAlertStyle | undefined => {
  if (n === undefined) return undefined;
  const map: Record<number, AnnouncementAlertStyle> = {
    0: AnnouncementAlertStyle.ANNOUNCEMENT,
    1: AnnouncementAlertStyle.ALERT
  };
  return map[n];
};

export const mapCreditPlanType = (n: number | undefined): CreditPlanType | undefined => {
  if (n === undefined) return undefined;
  const map: Record<number, CreditPlanType> = {
    0: CreditPlanType.PERSONALIZED,
    1: CreditPlanType.NON_PERSONALIZED
  };
  return map[n];
};

/**
 * Get the single-image URL used by non-card credit plan consumers.
 * This keeps the legacy fixed-size behavior for callers like side-panel thumbnails.
 * Card rendering uses `getCreditPlanCardRegionImageSources` instead.
 * @param regionCode (ex: CLE-NY, CLE-CA, etc..)
 * @param isTransitional used to distinguish between transitional/newly admitted vs experienced attorneys
 * @param orientation image orientation, either landscape or portrait
 * @returns string URL of the image
 */
export const getCreditPlanRegionImage = (
  regionCode: string,
  isTransitional: boolean,
  orientation: 'landscape' | 'portrait'
) => {
  // These fixed dimensions are intentionally preserved for non-card consumers.
  // Responsive card images are handled separately via `getCreditPlanCardRegionImageSources`.
  return orientation === 'landscape'
    ? buildCreditPlanRegionImageUrl(regionCode, isTransitional, orientation, 1280, 417)
    : buildCreditPlanRegionImageUrl(regionCode, isTransitional, orientation, 410, 615);
};

/**
 * Get responsive image sources for credit plan cards.
 * Returns a 1x source plus a 2x density candidate so the browser can choose
 * a sharper asset for high-DPI displays without changing card layout behavior.
 */
export const getCreditPlanCardRegionImageSources = (
  regionCode: string,
  isTransitional: boolean,
  orientation: 'landscape' | 'portrait'
) => {
  const oneX = CREDIT_PLAN_CARD_IMAGE_DIMENSIONS[orientation]['1x'];
  const twoX = CREDIT_PLAN_CARD_IMAGE_DIMENSIONS[orientation]['2x'];
  const src = buildCreditPlanRegionImageUrl(
    regionCode,
    isTransitional,
    orientation,
    oneX.width,
    oneX.height
  );
  const srcSet = `${src} 1x, ${buildCreditPlanRegionImageUrl(regionCode, isTransitional, orientation, twoX.width, twoX.height)} 2x`;

  return { src, srcSet };
};

/**
 * Get the credit plan card button label.
 * @param status
 * @returns
 */
export const getCreditPlanCardButtonLabel = (status: CreditPlanStatus) => {
  switch (status) {
    case CreditPlanStatus.STARTED:
    case CreditPlanStatus.EXECUTING:
      return (
        <span className="flex items-center gap-2">
          <LoadingDots dotSize="small" />
          Generating plan
        </span>
      );
    case CreditPlanStatus.FAILED:
    case CreditPlanStatus.COMPLETED:
      return (
        <span className="flex items-center gap-2 whitespace-nowrap">
          Browse plan <ChevronRightIcon className="-mr-2 size-4" />
        </span>
      );
  }
};

export const buildCreditPlanLicenseIdentifier = (
  indivSk: string | number,
  jurisdiction: string,
  flag?: string
) => {
  return `${indivSk}:${jurisdiction}:${flag ?? ''}`;
};

export const deconstructCreditPlanLicenseIdentifier = (licenseIdentifier: string) => {
  const parts = licenseIdentifier?.split(':') ?? [];
  const [indivSk = '', jurisdiction = '', flag = ''] = parts;
  return { indivSk, jurisdiction, flag };
};

export const shouldSkipPracticeAreaSteps = (indivSk?: string) =>
  existsCookie(buildCreditPlansCookieName(CREDIT_PLANS_PRACTICEAREA_COOLDOWN_COOKIE_NAME, indivSk));

export const hasTransitionalOrNewlyAdmittedFlag = (flags: string[]) =>
  flags.some(flag => {
    const normalizedFlag = flag.toLowerCase();
    return normalizedFlag.includes('transitional') || normalizedFlag.includes('newly');
  });

export const buildCreditPlansCookieName = (key: string, indivSk?: string) => {
  const userSegment = indivSk ? `_${indivSk}` : '';
  return `${key}${userSegment}`;
};

export const getJurisdictionDisclaimer = (content: IContent[], jurisdiction?: string) => {
  if (content && content.length > 0) {
    for (const item of content) {
      const block = item.contentLink?.expanded as JurisdictionSpecificMessageBlock;

      if (block && block.jurisdiction?.toLowerCase() == jurisdiction?.toLowerCase()) {
        return block.message;
      }
    }
  }
};

export const hasTransitionalFlagOption = (options: ICLEStateTransitionOption[]) => {
  return (
    options.some(option =>
      option.activatesFlags?.some(flag => flag.toLowerCase().includes('transitional'))
    ) || false
  );
};

export const getSelectedSpecialStatusFlags = (
  statuses: SpecialStatusSelector[],
  license?: UserTrackerData
) => {
  const selectedStatus = statuses.find(status => status.value.toLowerCase() === 'true');
  if (!selectedStatus) return [];

  const selectedFlag = (license?.jurisdiction?.cleStateTransitionOptions ?? [])
    .flatMap(option => Object.values(option.regionSpecialStatuses ?? {}))
    .find(status => status.name === selectedStatus.text)?.flag;

  return selectedFlag ? [selectedFlag] : [];
};

export const getJurisdictionFromTrackerLicense = (
  license: UserTrackerData,
  complianceFormFields?: ComplianceFormFields
): CreditPlanJurisdiction => {
  const flags = complianceFormFields?.ruleSetFlag ?? license.jurisdiction?.activeFlags ?? [];

  return {
    id: license.creditRegionShortDescription,
    creditRegion_SK: license.creditRegion_SK,
    compliancePeriodEndDate:
      complianceFormFields?.endDate || license.jurisdiction?.clePeriod?.endDate || '',
    flags,
    longDescription: license.creditRegionLongDescription,
    name: license.creditRegionLongDescription,
    shortDescription: license.creditRegionShortDescription
  } as CreditPlanJurisdiction;
};

export const replacePlaceholder = (text: string, placeholder: string, value: string) => {
  return templateString(text, { [placeholder]: value });
};

export const getCreditPlanItemSavedState = (
  item: Pick<CreditPlanResultItem, 'variationPk' | 'isSavedToUserLibrary' | 'userList'>,
  overrides: Record<string, boolean> = {}
) => {
  const pk = item.variationPk;
  if (!pk) return false;

  return (
    overrides[pk] ??
    item.isSavedToUserLibrary ??
    !!item.userList?.some(user => user.pk === pk && getIsSavedToLibrary(user))
  );
};

export const getCreditCalloutItems = (
  items: CreditPlanResultItem[],
  groupKey: string | null | undefined,
  plan: CreditPlanResponseModel['creditPlan'],
  jurisdictionId: string | null | undefined
) => {
  const requirements = (plan?.creditBreakdown?.creditBreakdownCreditTypes ?? []).flatMap(
    function flatten(requirement: CreditBreakdownCreditType): CreditBreakdownCreditType[] {
      return [requirement, ...(requirement.subRequirements?.flatMap(flatten) ?? [])];
    }
  );
  const requirementByKey = new Map(
    requirements.map(requirement => [requirement.key?.toLowerCase(), requirement])
  );
  const getAllowedTypes = (key: string, visited = new Set<string>()): string[] => {
    const normalizedKey = key.toLowerCase();
    if (visited.has(normalizedKey)) return [];

    const requirement = requirementByKey.get(normalizedKey);
    if (!requirement?.allowedTypes?.length) return [normalizedKey];

    const nextVisited = new Set(visited).add(normalizedKey);
    return requirement.allowedTypes.flatMap(type => getAllowedTypes(type, nextVisited));
  };
  const groupRequirement = requirementByKey.get(groupKey?.toLowerCase());
  const requiredSubRequirements: {
    allowedTypes: Set<string>;
    description: string;
    remaining: number;
  }[] = [];
  if (groupRequirement?.isRequired) {
    const addRequiredSubRequirements = (requirements: CreditBreakdownCreditType[]) => {
      requirements.forEach(requirement => {
        if (
          requirement.isRequired &&
          requirement.key &&
          Number.isFinite(requirement.amount) &&
          (requirement.amount ?? 0) > 0
        ) {
          requiredSubRequirements.push({
            allowedTypes: new Set(getAllowedTypes(requirement.key)),
            description: requirement.name ?? requirement.key,
            remaining: requirement.amount!
          });
        }
        addRequiredSubRequirements(requirement.subRequirements ?? []);
      });
    };
    addRequiredSubRequirements(groupRequirement.subRequirements ?? []);
  }

  return items.map(item => {
    const creditsByType = new Map<string, number>();
    item.credits?.forEach(credit =>
      credit.jurisdictions?.forEach(jurisdiction => {
        if (jurisdiction.jurisdiction !== jurisdictionId) return;

        jurisdiction.types?.forEach(type => {
          if (!type.type || !Number.isFinite(type.qty) || (type.qty ?? 0) <= 0) return;

          const typeKey = type.type.toLowerCase();
          creditsByType.set(typeKey, (creditsByType.get(typeKey) ?? 0) + type.qty!);
        });
      })
    );

    const descriptions = new Set<string>();
    creditsByType.forEach((quantity, type) => {
      requiredSubRequirements.forEach(requirement => {
        if (requirement.remaining <= 0 || !requirement.allowedTypes.has(type)) return;

        descriptions.add(requirement.description);
        const remaining = requirement.remaining - quantity;
        requirement.remaining = remaining < 1e-10 ? 0 : remaining;
      });
    });

    return [...descriptions].map(description => `Suggested to fulfill ${description}`);
  });
};

export const getTrackerUrl = (href?: string, jurisdictionKey?: string) => {
  if (!href || !jurisdictionKey) return href;

  const url = new URL(href, window.location.origin);
  url.searchParams.set(TrackerQueryParamKeys.REGION_KEY, jurisdictionKey);

  return url.toString();
};

export const getCreditsText = (
  amount: number,
  hasSubRequirements: boolean = false,
  isSubrequirement: boolean = false
) => {
  const credits = amount === 1 ? 'credit' : 'credits';
  const suffix = isSubrequirement ? 'required' : 'needed';
  return `${amount}${hasSubRequirements ? ' total' : ''} ${credits} ${suffix}`;
};
